import { useMemo } from 'react';

import {
  MerchantOrderTypeEnum,
  type MerchantOrderItemOptionsFilter,
} from '@/graphql/generatedTypes';
import { useMerchantOrderItemOptionsQuery } from '@/graphql/queries/merchantOrderItemOptions.generated';

import type { OrderKind } from '../components/details/types';
import { useContainerTypes } from './useContainerTypes';

export interface OrderItemOption {
  type_id: number;
  kind: 'container' | 'untracked';
  name: string;
  imageUrl: string | null;
  hexCode: string | null;
  purchasePriceCents: number;
  disabled: boolean;
  disabledUntil: string | null;
  orderKey: number;
}

interface UseOrderItemOptionsArgs {
  orderId: string;
  storeTextId: string | null | undefined;
  orderType: MerchantOrderTypeEnum | null | undefined;
  orderKind: OrderKind;
  includeAllContainers?: boolean;
  skip?: boolean;
}

export function useOrderItemOptions({
  orderId,
  storeTextId,
  orderType,
  orderKind,
  includeAllContainers = false,
  skip,
}: UseOrderItemOptionsArgs): {
  options: OrderItemOption[];
  loading: boolean;
} {
  const filter = useMemo<MerchantOrderItemOptionsFilter | null>(() => {
    if (!orderType) return null;
    return {
      merchant_order_id: orderId,
      store_text_id: storeTextId ?? undefined,
      order_type: orderType,
      include_all_containers: includeAllContainers,
    };
  }, [orderId, storeTextId, orderType, includeAllContainers]);

  const { data, loading } = useMerchantOrderItemOptionsQuery({
    variables: filter ? { filter } : undefined,
    skip: skip || !filter,
    fetchPolicy: 'cache-and-network',
  });

  const containerTypes = useContainerTypes();

  const options = useMemo<OrderItemOption[]>(() => {
    const raw = data?.merchantOrderItemOptions ?? [];
    const ctMap = new Map(containerTypes.map((ct) => [ct.id, ct]));
    const now = Date.now();

    return raw
      .filter((opt) => opt.type_id !== 0)
      .map((opt) => {
        const ct = opt.type_id >= 1 ? ctMap.get(opt.type_id) : undefined;

        // Outbound: only `resupply_disabled_until` exists on the schema — if it
        // points at a future date, the item is currently unavailable even when
        // `resupply_disabled` is false.
        const activeDisabledUntil =
          orderKind === 'outbound' &&
          opt.resupply_disabled_until &&
          new Date(opt.resupply_disabled_until).getTime() > now
            ? opt.resupply_disabled_until
            : null;

        const disabled =
          orderKind === 'inbound'
            ? opt.pickup_disabled === true
            : opt.resupply_disabled === true || activeDisabledUntil !== null;

        return {
          type_id: opt.type_id,
          kind:
            opt.type_id >= 1 ? ('container' as const) : ('untracked' as const),
          name: opt.name ?? ct?.name ?? `#${opt.type_id}`,
          imageUrl: ct?.imageUrl ?? null,
          hexCode: ct?.hexCode ?? null,
          purchasePriceCents: opt.purchase_price_cents,
          disabled,
          disabledUntil: activeDisabledUntil,
          orderKey: opt.order_key,
        };
      })
      .sort((a, b) => b.orderKey - a.orderKey);
  }, [data, containerTypes, orderKind]);

  return { options, loading };
}
